import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {IMomentDateFormat} from "libraries/interfaces/IMomentDateFormat";
import {IRemark} from "libraries/service/resources/booking/interfaces/IGetBookings";
import {EEditState, ENoteFormProp, INoteFormValue, INotepadCustomStyle, IRemarkForNotepad} from './notepad.interface';
import {INotepad} from "libraries/service/resources/booking/interfaces/notepad.interface";
import {Yii} from "libraries/interfaces/Yii";
import {DateService} from "libraries/date/date.service";
import {AbstractControl, FormArray, FormControl, FormGroup} from "@angular/forms";
import {UserAuthService} from 'libraries/service/user-auth/user-auth.service';

export const NOTEPAD_EDITOR_PERMISSION: string = 'shared.notepad.edit';

@Component({
    selector: 'notepad',
    templateUrl: './notepad.component.html',
    styleUrls: ['./notepad.component.scss','../booking-information/components/booking-remarks/components/shared/remark.scss'],
})
export class NotepadComponent implements OnChanges, OnDestroy, OnInit {
    @Input()
    public notes: IRemarkForNotepad[];

    @Input()
    public edit: boolean = false;

    @Input()
    public displayMode: 'CRUD' | 'CREATE' = 'CRUD';

    @Input()
    public customStyle: {
            colLeft: INotepadCustomStyle,
            colRight: INotepadCustomStyle,
    } = {
        colLeft: {
            width: "225px",
            backgroundColorOdd: "#fff",
            backgroundColorEven: "#fff"
        },
        colRight: {
            backgroundColorOdd: "#f9fafc",
            backgroundColorEven: "##f2f2f2"
        },
    };

    @Input()
    public isEditable: boolean = true;

    @Input()
    public isAlwaysEditable: boolean = false;

    @Input()
    public isEditPermission: boolean = false;

    @Input()
    public styleIsFull: boolean = false;

    @Input()
    public isLengthRestriction: boolean = false;

    @Input()
    public updateNotes: boolean = true;

    @Input()
    public isRichText: boolean = false;

    @Output()
    public readonly onEdit: EventEmitter<null> = new EventEmitter();

    @Output()
    public readonly onDestroy: EventEmitter<null> = new EventEmitter();

    @Output()
    public readonly onChangeNotes: EventEmitter<INotepad[]> = new EventEmitter();

    public readonly dateFormat: IMomentDateFormat = JSON.parse(sessionStorage.getItem('dateFormat'));
    public readonly addNoteLabel: string = Yii.t('wui-shared-js', "Ajouter une note");

    public readonly editState: typeof EEditState = EEditState;
    public readonly noteFormProp: typeof ENoteFormProp = ENoteFormProp;

    public readonly notesFormArrayName: string = 'notes';

    public formGroup: FormGroup;

    get notesFormArray(): FormArray {
        return this.formGroup.get(this.notesFormArrayName) as FormArray;
    }

    private requesterUserId: number;
    private requesterName: string;

    constructor(
        private readonly userAuthService: UserAuthService,
        private readonly dateService: DateService
    ) {}

    public ngOnInit(): void {
        if (this.isAlwaysEditable) {
            this.requesterName = this.userAuthService.getRequesterName();
            this.requesterUserId = this.userAuthService.getRequester() ? this.userAuthService.getRequester().user : null;
        }

        this.initializeFormGroup((this.notes||[]));
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.notes && changes.notes.currentValue && this.updateNotes) {
            this.initializeFormGroup(changes.notes.currentValue);
        }
    }

    /**
     * Allows to cancel edition on destroy
     */
    public ngOnDestroy(): void {
        this.onDestroy.emit();
    }

    public editNote(noteAbstractControl: AbstractControl): void {
        const noteFormGroup: FormGroup = noteAbstractControl as FormGroup;

        const notePatch: Partial<INoteFormValue> = {
            editState: EEditState.edit,
            noteWhenLastUpdated: this.dateService.getNowAsIsoString()
        };
        noteFormGroup.patchValue(notePatch);

        this.onEdit.emit();
        this.isAlwaysEditable && this.onChangeNotes.emit(this.extractNotes());
    }

    public cancelEdition(): void {
        this.initializeFormGroup((this.notes||[]));
    }

    public deleteNote(noteIndex: number): void {
        this.notesFormArray.removeAt(noteIndex);

        this.onEdit.emit();
        this.isAlwaysEditable && this.onChangeNotes.emit(this.extractNotes());
    }

    public cancelEditNote(noteIndex: number): void {
        const noteFormGroup: FormGroup = this.notesFormArray.at(noteIndex) as FormGroup;
        const noteFormValue: INoteFormValue = noteFormGroup.value as INoteFormValue;

        if (noteFormValue.isNew) {
            this.notesFormArray.removeAt(noteIndex);
        } else {
            const originalNote: IRemarkForNotepad = this.findNote(noteFormValue.noteXId);
            const notePatchValue: Partial<INoteFormValue> = {
                noteText: this.getAsString(originalNote.text),
                noteWhenLastUpdated: originalNote.whenLastUpdated,
                editState: EEditState.initial
            };
            noteFormGroup.patchValue(notePatchValue);
        }
    }

    public addNote(): void {
        this.notesFormArray.push(this.mapNoteAsFormGroup(this.createNewNote(), true));
        this.onEdit.emit();
    }

    public extractValues(): IRemark[] {
        return this.notesFormArray.value;
    }
    public extractNotes(): INotepad[] {
        const noteFormValue: INoteFormValue[] = this.notesFormArray.value;
        const returnNote: INotepad[] = noteFormValue.map((noteForm: INoteFormValue):INotepad => ({
            ...this.findNote(noteForm.noteXId),
            key: noteForm.key,
            xId: noteForm[ENoteFormProp.noteXId],
            text: this.getAsString(noteForm.noteText),
            whenLastUpdated: noteForm.noteWhenLastUpdated,
            authorId: noteForm.noteAuthorId,
            name: noteForm.noteAuthorName,
        }));
        return returnNote;
    }

    private getAsString(textOrArray: string|string[]): string {
        return Array.isArray(textOrArray) ? textOrArray.join(' ') : textOrArray;
    }

    private findNote(xIdToSearch: string): IRemarkForNotepad {
        return (this.notes||[]).find((note: IRemarkForNotepad) => xIdToSearch === note.noteXId);
    }

    private buildNewXId(): string {
        let noteWithMaxIndex: IRemark;
        if ((this.notes||[]).length) {
            noteWithMaxIndex = this.notes.reduce(
                (previousValue: IRemark, currentValue: IRemark) => previousValue.noteXId > currentValue.noteXId ? previousValue : currentValue);
        }

        return noteWithMaxIndex ? (parseInt(noteWithMaxIndex.noteXId, 10) + 1).toString() : '1';
    }

    private initializeFormGroup(inputNotes: IRemark[]): void {
        const newNotes: IRemark[] = inputNotes || [];
        this.formGroup = new FormGroup({
            [this.notesFormArrayName]: new FormArray(newNotes.map((value: IRemark) => this.mapNoteAsFormGroup(value, false)))
        });
    }

    private mapNoteAsFormGroup(note: IRemarkForNotepad, newNote: boolean = false): FormGroup {
        return new FormGroup({
            // @ts-ignore
            [ENoteFormProp.noteXId]: new FormControl(note.xId || note.noteXId),
            // @ts-ignore
            [ENoteFormProp.key]: new FormControl(note.key),
            // @ts-ignore
            [ENoteFormProp.noteText]: new FormControl(this.getAsString(note.text || note.noteText)),
            // @ts-ignore
            [ENoteFormProp.noteWhenLastUpdated]: new FormControl(note.whenLastUpdated || note.noteWhenLastUpdated),
            // @ts-ignore
            [ENoteFormProp.noteWhoLastUpdated]: new FormControl(note.whoLastUpdated || note.noteWhoLastUpdated),
            // @ts-ignore
            [ENoteFormProp.noteAuthorId]: new FormControl(note.authorId || note.noteAuthorId),
            // @ts-ignore
            [ENoteFormProp.noteAuthorName]: new FormControl(note.name || note.noteAuthorName),
            [ENoteFormProp.isNew] : new FormControl(newNote),
            [ENoteFormProp.editState]: new FormControl(newNote || this.isAlwaysEditable ? EEditState.edit : EEditState.initial),
        });
    }

    private createNewNote(): IRemarkForNotepad {
        const xId: string = this.buildNewXId();
        const date: string = this.dateService.getNowAsIsoString();
        return {
            xId,
            text: [],
            whenLastUpdated: date,
            authorId: this.requesterUserId,
            name: this.requesterName,
        };
    }
}
